import React from 'react';
import { BrandTokenProvider } from '@charitystack/design-tokens/provider';

import { generateBrandColors } from './HSB';

/**
 * @deprecated Use `BrandTokenProvider` from `@charitystack/design-tokens/provider`
 * directly and hand it the container you want branded.
 *
 * CON-5 collapsed the four divergent brand writers into that one scoped
 * provider. This file survives only as the binding that (a) keeps the two
 * donation-elements mount sites (`AddToCart.jsx:328`, `AddToCartSheet.jsx:1047`)
 * working byte-for-byte without editing them in this PR, and (b) supplies the
 * HSB expander so the token package stays free of `packages/shared`.
 *
 * SCOPE — why `document.body` and not a wrapper element.
 * The provider is scoped: it never writes `document.documentElement` (D6). The
 * scope still has to be an ancestor of every READER, and both mount sites here
 * render subtrees that portal OUT of their React parent:
 *   - `AddToCartSheet` renders a shadcn `<Sheet>`, i.e. `@radix-ui/react-dialog`'s
 *     `Portal`, whose default container is `document.body`;
 *   - the branded shadcn buttons inside that sheet read `var(--cs-color-primary)`
 *     and `var(--cs-color-primary-foreground)`.
 * A layout-neutral wrapper element around `children` would therefore leave the
 * portalled sheet unbranded — a visible regression. `document.body` is the
 * smallest element that provably contains every reader in these two surfaces,
 * and each element renders in its own iframe document, so this is already a
 * per-widget scope. Tightening it to a real container needs `container` props
 * threaded through Radix/vaul, which is a follow-up (see
 * docs/initiatives/new/design-system/implementation/con-5-brand-provider.md).
 *
 * Pass an explicit `scope` to override.
 */
export function BrandColorProvider({ baseColor, brandColors, scope, children }) {
  const element =
    scope || (typeof document !== 'undefined' ? document.body : null);

  return (
    <BrandTokenProvider
      scope={element}
      palette={brandColors}
      color={baseColor}
      expand={generateBrandColors}
      // D16's fail-loud check. Dev-only inside `auditBrandedSurfaces`: it walks
      // `.bg-primary` / `.text-primary` / `[data-cs-branded]`, reads the
      // resolved `--cs-color-primary`, and warns for any that came back unset or
      // still the legacy `#69995E`. These two surfaces are where the branded
      // shadcn buttons and the hand-rolled `.bg-primary` utility actually live.
      audit
    >
      {children}
    </BrandTokenProvider>
  );
}
